import React, { useContext, useCallback } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { AuthContext } from '../../../contexts/AuthContext';
import { BrandLogo } from './components/BrandLogo';
import { Navigation, NavItem } from './components/Navigation';
import { UserMenu } from './components/UserMenu';
import { DarkModeToggle } from './components/DarkModeToggle';
import { WorkspaceSwitcher } from './components/WorkspaceSwitcher';
import Logger from '../../../utils/logger';

const logger = Logger.create('Header');

export interface HeaderProps {
  className?: string;
}

/**
 * Header component matching GraymatterLab Agent Workshop design.
 * Features: Brand logo, navigation links, user menu with avatar/role, dark mode toggle.
 */
export const Header: React.FC<HeaderProps> = ({ className = '' }) => {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = useCallback(async () => {
    if (authContext) {
      try {
        await authContext.signOut();
        navigate('/login');
      } catch (error) {
        logger.error('Sign out failed:', error);
      }
    }
  }, [authContext, navigate]);

  // Define navigation links
  const navLinks: NavItem[] = [
    { to: '/dashboard', label: 'Dashboard', visible: true },
    { to: '/projects/new', label: 'Design New Agent', visible: true },
    { to: '/guide', label: 'User Guide', visible: true },
    { to: '/admin', label: 'Admin', visible: authContext?.isAdmin ?? false },
  ];

  return (
    <header
      className={`
        bg-white dark:bg-secondary-900
        border-b border-secondary-200 dark:border-secondary-700
        shadow-soft dark:shadow-none
        ${className}
      `
        .trim()
        .replace(/\s+/g, ' ')}
    >
      <div className="container mx-auto max-w-7xl flex items-center justify-between px-6 py-4">
        {/* Left: Brand Logo */}
        <BrandLogo />

        {/* Right: Navigation + User Menu + Dark Mode Toggle */}
        <div className="flex items-center gap-6">
          {authContext?.currentUser ? (
            <>
              {/* Navigation Links */}
              <Navigation links={navLinks} currentPath={location.pathname} />

              {/* Workspaces Switcher */}
              <WorkspaceSwitcher />

              {/* Dark Mode Toggle */}
              <DarkModeToggle />

              {/* User Menu */}
              <UserMenu
                user={authContext.currentUser}
                systemRole={authContext.systemRole}
                isAdmin={authContext.isAdmin ?? false}
                onSignOut={handleSignOut}
              />
            </>
          ) : (
            <>
              {/* Dark Mode Toggle for unauthenticated users */}
              <DarkModeToggle />

              {/* Sign In Link */}
              <RouterLink
                to="/login"
                className="
                  px-4 py-2 text-sm font-medium rounded-md transition-colors
                  text-secondary-600 dark:text-secondary-300
                  hover:text-primary-600 dark:hover:text-primary-400
                  hover:bg-secondary-100 dark:hover:bg-secondary-800
                "
              >
                Sign In
              </RouterLink>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
